// ── GitHub 开发活跃度 ─────────────────────────────────
// GitHub commit_activity 是异步计算的（首次返回 202），故单独加 5s 超时
const ghFetch = (url) =>
  fetch(url, { signal: AbortSignal.timeout(5000) })
    .then(r => r.status === 200 ? r.json() : null)
    .catch(() => null);

/**
 * 查 README.md 的最早 commit 日期 — 作为「首次文档公开」的可靠兜底
 * 当 Wayback 在 docs.domain / gitbook 等都查不到时使用
 */
export async function fetchGitHubReadmeFirstCommit(owner, repo) {
  if (!owner || !repo) return null;
  try {
    // 试 README.md 和 README（无后缀），再试 docs/ 目录
    for (const path of ['README.md', 'README', 'docs/README.md', 'docs/index.md']) {
      const commits = await ghFetch(
        `https://api.github.com/repos/${owner}/${repo}/commits?path=${encodeURIComponent(path)}&per_page=100`
      );
      if (Array.isArray(commits) && commits.length > 0) {
        // commits 按时间倒序，最后一个是最早的
        const first = commits[commits.length - 1];
        const date = first?.commit?.author?.date || first?.commit?.committer?.date;
        if (date) {
          return {
            date: date.slice(0, 10),
            url: `https://github.com/${owner}/${repo}/blob/main/${path}`,
            path,
          };
        }
      }
    }
    return null;
  } catch { return null; }
}

export async function fetchGithubSignal(details) {
  const sm = details.social_media || {};
  let githubUrl = null;
  for (const val of Object.values(sm)) {
    if (typeof val === "string" && val.toLowerCase().includes("github.com")) {
      githubUrl = val; break;
    }
  }
  if (!githubUrl) return null;

  const m = githubUrl.match(/github\.com\/([^\/\s]+)(?:\/([^\/\s\?#]+))?/);
  if (!m) return null;
  const owner = m[1];
  const repo  = m[2]?.replace(/\.git$/, "");

  try {
    if (repo) {
      const [commits, repoInfo, releases, contributors] = await Promise.all([
        ghFetch(`https://api.github.com/repos/${owner}/${repo}/stats/commit_activity`),
        ghFetch(`https://api.github.com/repos/${owner}/${repo}`),
        ghFetch(`https://api.github.com/repos/${owner}/${repo}/releases?per_page=100`),
        // 贡献者数量：多人贡献 = 真实团队信号；单人仓库是风险信号
        ghFetch(`https://api.github.com/repos/${owner}/${repo}/contributors?per_page=10&anon=0`),
      ]);
      const weeks = Array.isArray(commits) ? commits.slice(-4) : [];
      const firstRelease = Array.isArray(releases) && releases.length > 0
        ? releases[releases.length - 1] : null;
      const contributorCount = Array.isArray(contributors) ? contributors.length : null;
      return {
        url: githubUrl, owner, repo,
        stars: repoInfo?.stargazers_count ?? null,
        isFork: repoInfo?.fork === true,           // fork 的仓库代码来源存疑
        forksCount: repoInfo?.forks_count ?? null,
        commits4w: weeks.reduce((s, w) => s + (w.total || 0), 0),
        weeklyCommits: weeks.map(w => w.total || 0),
        lastPushed: repoInfo?.pushed_at || null,
        repoCreatedAt: repoInfo?.created_at || null,
        contributorCount,                          // null=未知, 1=单人, >3=真实团队
        firstReleaseAt: firstRelease?.published_at || firstRelease?.created_at || null,
        firstReleaseTag: firstRelease?.tag_name || null,
      };
    } else {
      const repos = await ghFetch(`https://api.github.com/orgs/${owner}/repos?sort=pushed&per_page=5`);
      if (!Array.isArray(repos) || repos.length === 0) return null;
      // 取最早创建的 repo 作为 org 创建时间
      const earliest = [...repos].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''))[0];
      // 查 org 内最早 repo 的首次 release
      let firstRelease = null;
      if (earliest) {
        const rels = await ghFetch(`https://api.github.com/repos/${owner}/${earliest.name}/releases?per_page=100`);
        if (Array.isArray(rels) && rels.length > 0) firstRelease = rels[rels.length - 1];
      }
      return {
        url: githubUrl, owner, repo: null,
        stars: repos.reduce((s, r) => s + (r.stargazers_count || 0), 0),
        commits4w: null,
        weeklyCommits: [],
        lastPushed: repos[0]?.pushed_at || null,
        repoCreatedAt: earliest?.created_at || null,
        firstReleaseAt: firstRelease?.published_at || firstRelease?.created_at || null,
        firstReleaseTag: firstRelease?.tag_name || null,
      };
    }
  } catch { return null; }
}
